/**
 * Bulk Upload Service
 * Handles communication with the backend bulk upload API
 */

import { authenticatedFetch } from '../lib/apiClient';
import { supabase } from '../lib/supabaseClient';

const MAX_FILES_PER_SESSION = 20;

export interface BulkSession {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  totalFiles: number;
  processedFiles: number;
  successfulFiles: number;
  failedFiles: number;
  progress: number;
  createdAt: string;
  completedAt?: string;
}

export interface BulkResumeResult {
  id: string;
  fileName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  matchScore?: number;
  parsedData?: any;
  matchResult?: any;
  error?: string;
  applicationId?: string;
}

export interface BulkStatusResponse {
  success: boolean;
  session: BulkSession;
  resumes: BulkResumeResult[];
}

/**
 * Helper to safely parse JSON response or return error text
 */
async function parseResponseSafe(response: Response): Promise<any> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    // If not JSON, return as error message
    return { error: text || `HTTP ${response.status}` };
  }
}

/**
 * Create a new bulk upload session
 */
export async function createBulkSession(jobId: string): Promise<{ sessionId: string; isExisting: boolean }> {
  const response = await authenticatedFetch('/api/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({
      action: 'create-session',
      jobId
    })
  });

  if (!response.ok) {
    const error = await parseResponseSafe(response);
    throw new Error(error.message || error.error || 'Failed to create session');
  }

  const data = await response.json();
  return {
    sessionId: data.sessionId,
    isExisting: data.isExisting || false
  };
}

/**
 * Upload files to storage and add to session
 */
export async function uploadFilesToSession(
  sessionId: string,
  files: File[],
  entityId: string,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ filesAdded: number; totalFiles: number }> {
  if (files.length > MAX_FILES_PER_SESSION) {
    throw new Error(`Maximum ${MAX_FILES_PER_SESSION} files allowed per session`);
  }

  const uploadedFiles: { fileName: string; filePath: string; fileSize: number }[] = [];

  // Upload each file to storage
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileName = `${sessionId}/${Date.now()}-${file.name}`;

    const { error: uploadError } = await supabase.storage
      .from('resumes')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error(`Failed to upload ${file.name}:`, uploadError);
      throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
    }

    uploadedFiles.push({
      fileName: file.name,
      filePath: fileName,
      fileSize: file.size
    });

    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }

  // Add files to session
  const response = await authenticatedFetch('/api/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({
      action: 'add-files',
      sessionId,
      files: uploadedFiles
    })
  });

  if (!response.ok) {
    const error = await parseResponseSafe(response);
    throw new Error(error.message || error.error || 'Failed to add files to session');
  }

  const data = await response.json();
  return {
    filesAdded: data.filesAdded,
    totalFiles: data.totalFiles
  };
}

/**
 * Start processing resumes in a session
 */
export async function startProcessing(sessionId: string): Promise<void> {
  const response = await authenticatedFetch('/api/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({
      action: 'start-processing',
      sessionId
    })
  });

  if (!response.ok) {
    const error = await parseResponseSafe(response);
    throw new Error(error.message || error.error || 'Failed to start processing');
  }
}

/**
 * Get session status
 */
export async function getSessionStatus(sessionId: string): Promise<BulkStatusResponse> {
  const response = await authenticatedFetch('/api/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({
      action: 'get-status',
      sessionId
    })
  });

  if (!response.ok) {
    const error = await parseResponseSafe(response);
    throw new Error(error.message || error.error || 'Failed to get session status');
  }

  return await response.json();
}

/**
 * Cancel a session
 */
export async function cancelSession(sessionId: string): Promise<void> {
  const response = await authenticatedFetch('/api/bulk-upload', {
    method: 'POST',
    body: JSON.stringify({
      action: 'cancel-session',
      sessionId
    })
  });

  if (!response.ok) {
    const error = await parseResponseSafe(response);
    throw new Error(error.message || error.error || 'Failed to cancel session');
  }
}

/**
 * Get active sessions for current user
 */
export async function getActiveSessions(entityId: string): Promise<BulkSession[]> {
  const { data, error } = await supabase
    .from('bulk_upload_sessions')
    .select('*')
    .eq('entity_id', entityId)
    .in('status', ['pending', 'processing'])
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching active sessions:', error);
    return [];
  }

  return (data || []).map((s: any) => ({
    id: s.id,
    status: s.status,
    totalFiles: s.total_files,
    processedFiles: s.processed_files,
    successfulFiles: s.successful_files,
    failedFiles: s.failed_files,
    progress: s.total_files > 0 ? Math.round((s.processed_files / s.total_files) * 100) : 0,
    createdAt: s.created_at,
    completedAt: s.completed_at
  }));
}

export const MAX_BULK_FILES = MAX_FILES_PER_SESSION;
